/**
 * IndexedDB-backed key store for E2E encryption keys.
 *
 * DB: go4school_e2e_<userId>  (scoped per authenticated user)
 * Stores:
 *   - keys: { id: "publicKey"|"privateKey", value: base64 string }
 *   - conversationKeys: { id: conversationId, value: Uint8Array }
 */

const DB_PREFIX = "go4school_e2e_"
const DB_VERSION = 1
const KEYS_STORE = "keys"
const CONV_KEYS_STORE = "conversationKeys"

let _currentUserId = null

/**
 * Set the current user ID. Must be called before any other
 * key store operation so the DB is scoped to the right user.
 *
 * @param {number|string} userId
 */
export function setCurrentUser(userId) {
  _currentUserId = userId
}

/**
 * Open (or create) the user-scoped IndexedDB database.
 * @returns {Promise<IDBDatabase>}
 */
function openDB() {
  if (!_currentUserId) {
    return Promise.reject(new Error("[E2E KeyStore] No user ID set — call setCurrentUser first"))
  }

  const dbName = `${DB_PREFIX}${_currentUserId}`

  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, DB_VERSION)

    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(KEYS_STORE)) {
        db.createObjectStore(KEYS_STORE, { keyPath: "id" })
      }
      if (!db.objectStoreNames.contains(CONV_KEYS_STORE)) {
        db.createObjectStore(CONV_KEYS_STORE, { keyPath: "id" })
      }
    }

    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

/**
 * Generic get from a store.
 * @param {string} storeName
 * @param {string|number} key
 * @returns {Promise<any|null>}
 */
async function getItem(storeName, key) {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readonly")
    const store = tx.objectStore(storeName)
    const req = store.get(key)
    req.onsuccess = () => resolve(req.result?.value ?? null)
    req.onerror = () => reject(req.error)
  })
}

/**
 * Generic put into a store.
 * @param {string} storeName
 * @param {string|number} key
 * @param {any} value
 * @returns {Promise<void>}
 */
async function putItem(storeName, key, value) {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite")
    const store = tx.objectStore(storeName)
    store.put({ id: key, value })
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

// ── Key pair operations ──

export async function getPublicKey() {
  return getItem(KEYS_STORE, "publicKey")
}

export async function getPrivateKey() {
  return getItem(KEYS_STORE, "privateKey")
}

export async function saveKeyPair(publicKey, privateKey) {
  await putItem(KEYS_STORE, "publicKey", publicKey)
  await putItem(KEYS_STORE, "privateKey", privateKey)
}

export async function hasKeyPair() {
  const pub = await getPublicKey()
  return pub !== null
}

// ── Conversation key operations ──

export async function getConversationKey(conversationId) {
  return getItem(CONV_KEYS_STORE, conversationId)
}

export async function saveConversationKey(conversationId, keyBytes) {
  await putItem(CONV_KEYS_STORE, conversationId, keyBytes)
}
